import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { AppDispatch, RootState } from '../../stateStore'
import { ApiLoadingState } from '../../middleware/api/apiList'
import { ValidationError } from '../../types/commonTypes'
import { IsVendorAvailable } from '../../types/Login/authenticationTypes'
import vendorResetApi from '../../middleware/api/VendorResetPswd/vendorresetApi'

export interface VendorResetCredentials {
  username: string
  newPassword: string
  tenantKey: string
}

export interface VendorResetState {
  isLoading: ApiLoadingState
  isVendorAvailable?: boolean
  error?: ValidationError | null
}

const initialVendorResetState: VendorResetState = {
  isLoading: ApiLoadingState.idle,
  isVendorAvailable: undefined,
  error: null,
}

const authenticateUser = createAsyncThunk<
  { isVendorAvailable: IsVendorAvailable } | undefined,
  VendorResetCredentials,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'vendorReset/authenticateUser',
  async (credentials: VendorResetCredentials, thunkApi) => {
    try {
      return await vendorResetApi.authenticateVendor(
        credentials.username,
        credentials.newPassword,
        // credentials.tenantKey,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const vendorResetSlice = createSlice({
  name: 'vendorReset',
  initialState: initialVendorResetState,
  reducers: {
    setVendorReset: (state, action) => {
      return { ...state, ...action.payload }
    },
    clearVendorReset: (state) => {
      return { ...state, ...initialVendorResetState }
    },
    clearError: (state) => {
      state.error = null
    },
    clearLoading: (state) => {
      state.isLoading = ApiLoadingState.idle
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload as ApiLoadingState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateUser.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
        state.error = null
      })
      .addCase(authenticateUser.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.isVendorAvailable =
          action.payload?.isVendorAvailable.isVendorAvailable
        state.error = null
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      })
  },
})

const selectIsLoading = (state: RootState): ApiLoadingState =>
  state.vendorReset.isLoading
const selectError = (state: RootState): ValidationError | null | undefined =>
  state.vendorReset.error
const selectIsVendorAvailable = (state: RootState): boolean | undefined =>
  state.vendorReset.isVendorAvailable

const vendorResetThunk = {
  authenticateUser,
}

const vendorResetSelectors = {
  selectError,
  selectIsLoading,
  selectIsVendorAvailable,
}

export const vendorResetService = {
  ...vendorResetThunk,
  actions: vendorResetSlice.actions,
  selectors: vendorResetSelectors,
}

export default vendorResetSlice.reducer
